import React, { useState } from 'react';
import { ApprovalRequest } from '../../types';
import { CheckCircle, XCircle, MessageSquare, Clock, User, AlertTriangle, Send, ArrowUp } from 'lucide-react';

interface ApprovalControlsProps {
  approval: ApprovalRequest;
  onApprove: (comment?: string) => Promise<void>;
  onReject: (comment: string) => Promise<void>;
  className?: string;
}

const ApprovalControls: React.FC<ApprovalControlsProps> = ({
  approval,
  onApprove,
  onReject,
  className = ''
}) => {
  const [comment, setComment] = useState('');
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  const handleApprove = async () => {
    if (comment.trim() || !showCommentForm) {
      setLoading(true);
      try {
        await onApprove(comment.trim() || undefined);
        setComment('');
        setShowCommentForm(false);
      } catch (error) {
        console.error('Failed to approve:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setShowCommentForm(true);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setShowCommentForm(true);
      return;
    }
    
    setLoading(true);
    try {
      await onReject(comment.trim());
      setComment('');
      setShowCommentForm(false);
    } catch (error) {
      console.error('Failed to reject:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (actionType: 'approve' | 'reject') => {
    setAction(actionType);
    if (actionType === 'reject') {
      setShowCommentForm(true);
    }
  };

  const formatWaitingTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return `${days}d ${hours}h`;
    }
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Header */}
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Approval Actions</h3>
        <div className="text-sm text-gray-600">
          Review the changes and take action
        </div>
      </div>

      {/* Approval Info */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center space-x-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-700">
            Waiting for {formatWaitingTime(approval.waitingTime)}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <User className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-700">
            Workflow: {approval.workflowId}
          </span>
        </div>
        
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          approval.priority === 'high' 
            ? 'bg-red-100 text-red-800'
            : approval.priority === 'medium'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-green-100 text-green-800'
        }`}>
          {approval.priority === 'high' && <AlertTriangle className="w-3 h-3 mr-1" />}
          {approval.priority.toUpperCase()} PRIORITY
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-b">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
        <div className="space-y-2">
          <button
            onClick={handleApprove}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {loading && action === 'approve' ? 'Approving...' : 'Approve'}
          </button>
          
          <button
            onClick={() => handleQuickAction('reject')}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </button>
        </div>
      </div>

      {/* Comment Form */}
      <div className="flex-1 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">Add Comment</h4>
          {!showCommentForm && (
            <button
              onClick={() => setShowCommentForm(true)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Add comment
            </button>
          )}
        </div>
        
        {(showCommentForm || comment) && (
          <div className="space-y-3">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={action === 'reject' ? 'Please explain why you are rejecting this approval...' : 'Add your comments or feedback...'}
              className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
            
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {action === 'reject' ? 'Comment required for rejection' : 'Comment is optional'}
              </div>
              <div className="text-xs text-gray-500">
                {comment.length}/500
              </div>
            </div>
            
            <div className="flex space-x-2">
              {action === 'approve' && (
                <button
                  onClick={handleApprove}
                  disabled={loading || comment.length > 500}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {loading ? 'Approving...' : 'Approve with Comment'}
                </button>
              )}
              
              {action === 'reject' && (
                <button
                  onClick={handleReject}
                  disabled={loading || !comment.trim() || comment.length > 500}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {loading ? 'Rejecting...' : 'Reject with Comment'}
                </button>
              )}
              
              {!action && comment && (
                <button
                  onClick={() => {
                    // Just add comment without approval/rejection
                    setComment('');
                    setShowCommentForm(false);
                  }}
                  disabled={loading || !comment.trim() || comment.length > 500}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Add Comment
                </button>
              )}
              
              <button
                onClick={() => {
                  setComment('');
                  setShowCommentForm(false);
                  setAction(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Escalation Options */}
      <div className="p-4 border-t bg-gray-50">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Need Help?</h4>
        <div className="space-y-2">
          <button className="w-full flex items-center justify-center px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowUp className="w-4 h-4 mr-2" />
            Escalate to Senior Reviewer
          </button>
          
          <button className="w-full flex items-center justify-center px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
            <MessageSquare className="w-4 h-4 mr-2" />
            Request More Information
          </button>
        </div>
        
        <div className="mt-3 text-xs text-gray-500">
          Use escalation if you need additional context or senior approval
        </div>
      </div>
    </div>
  );
};

export default ApprovalControls;
